import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
import { ubicacionesMexico } from '../data/mockData';
import MessageButton from '../components/MessageButton';
import BlockButton from '../components/BlockButton';
import AdminButtons from '../components/AdminButtons';
import config from '../config/config';

import { Search, Filter, MapPin, Calendar, Heart, MessageCircle, Eye } from 'lucide-react';

const SearchPartner = () => {
  const { user: currentUser } = useAuth();
  const location = useLocation();
  const isAdmin = config.isAdmin(currentUser);
  
  // Calcular edades predeterminadas basadas en el usuario
  const getDefaultMinAge = () => {
    if (!currentUser?.age) return 18;
    const minAge = currentUser.age - 10;
    return minAge < 18 ? 18 : minAge;
  };
  
  const getDefaultMaxAge = () => {
    if (!currentUser?.age) return 100;
    return currentUser.age + 10;
  };

  const initialSoy = currentUser?.sexoBusca ? config.extractSoyFromSexoBusca(currentUser.sexoBusca) : '';
  const initialBusco = currentUser?.sexoBusca ? config.extractBuscoFromSexoBusca(currentUser.sexoBusca) : '';

  const [filters, setFilters] = useState({
    minAge: getDefaultMinAge(),
    maxAge: getDefaultMaxAge(),
    state: currentUser?.state || '', // Auto-poblar con el estado del usuario
    hasPhoto: false,
    // Keep separate fields in the UI; sexoBusca (combined) will be built when sending
    soyGender: initialSoy,
    buscoGender: initialBusco,
    sexoBusca: currentUser?.sexoBusca || ''
  });
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [apiStatus, setApiStatus] = useState('idle');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12, // Cambiado a 12 por página
    total: 0,
    pages: 0
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [usernameSearch, setUsernameSearch] = useState('');
  const [isUsernameSearching, setIsUsernameSearching] = useState(false);
  const [blockedUsers, setBlockedUsers] = useState(new Set()); // Set de IDs de usuarios bloqueados (para UI local)
  const [notification, setNotification] = useState(null); // Notificación temporal
  const [filtersApplied, setFiltersApplied] = useState(false); // Track si los filtros ya se aplicaron

  // Forzar scroll hacia arriba cuando se carga la página, pero no en búsquedas
  useEffect(() => {
    // Solo hacer scroll al inicio si no hay resultados de búsqueda
    if (searchResults.length === 0 && !isSearching && !isUsernameSearching) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  // El backend manejará automáticamente la compatibilidad de género
  // Solo enviamos los filtros básicos y el backend filtra usuarios compatibles

  // Cargar usuarios automáticamente al entrar a la pestaña (sin filtros)
  useEffect(() => {
    filterUsers(1, false); // false = no aplicar filtros
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Procesar búsqueda desde el home si existe
  useEffect(() => {
    if (location.state?.usernameSearch) {
      const searchTerm = location.state.usernameSearch;
      setUsernameSearch(searchTerm);
      // Ejecutar búsqueda automáticamente
      handleUsernameSearch(searchTerm);
      // Limpiar el state de navegación
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // El backend maneja automáticamente el filtrado de usuarios bloqueados
  // Solo mantenemos el estado local para efectos de UI

  // Manejar notificaciones temporales
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000); // Ocultar después de 5 segundos
      
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Filtrar usuarios usando el backend
  const filterUsers = async (page = currentPage, applyFilters = false) => {
    setIsSearching(true);
    setApiStatus('searching');
    
    try {
      let payload = {};
      
      // Para admin: Solo aplicar filtros si applyFilters es true
      // Para usuarios normales: aplicar filtros siempre
      if (isAdmin ? applyFilters : true) {
        // Build payload: prefer soyGender+buscoGender (UI), fallback to filters.sexoBusca
        payload = { ...filters };
        const soy = payload.soyGender || '';
        const busco = payload.buscoGender || '';
        if (soy === 'No binario' || busco === 'No binario') {
          payload.sexoBusca = 'No binario';
        } else if (soy && busco) {
          payload.sexoBusca = config.buildSexoBusca(soy, busco);
        } // otherwise leave payload.sexoBusca as-is (may be '')
      }

      const response = await apiService.searchUsers(payload, page, pagination.limit);

      // Transformar resultados al formato del frontend
      const transformedResults = response.users.map(user => {
        const gender = user.sex === 'male' ? 'Hombre' : 'Mujer';
        const hasPhoto = !!(user.profilePicture || user.profile_picture);
        
        return {
          id: user.id,
          username: user.username,
          email: user.email,
          name: user.firstName || user.username || 'Usuario',
          age: user.age,
          gender: gender,
          state: user.location || 'Ciudad de México',
          description: user.bio || '',
          hasPhoto: hasPhoto,
          photoUrl: hasPhoto ? (user.profilePicture || user.profile_picture) : config.getDefaultAvatar(gender),
          isOnline: user.isOnline === 1 || user.isOnline === true,
          lastSeen: user.lastLogin || user.last_login,
          // Nuevos campos
          estadoCivil: user.estadoCivil,
          educacion: user.educacion,
          ocupacion: user.ocupacion,
          descripcionFisica: user.descripcionFisica
        };
      });


      // Solo aplicar filtro por foto en frontend (temporal hasta que el backend lo soporte)
      let filteredResults = transformedResults;
      
      if (filters.hasPhoto) {
        const usersWithPhoto = filteredResults.filter(user => user.hasPhoto);
        filteredResults = usersWithPhoto;
      }
      
      // Establecer resultados
      if (page === 1) {
        setSearchResults(filteredResults);
      } else {
        setSearchResults(prev => [...prev, ...filteredResults]);
      }

      // Actualizar información de paginación
             setPagination(response.pagination);
       setCurrentPage(page);
       setApiStatus('success');
    } catch (error) {
      setApiStatus('error');
       if (page === 1) {
        setSearchResults([]);
      }
    } finally {
      setIsSearching(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Update fields normally for non-checkbox inputs
    const newFilters = {
      ...filters,
      [name]: type === 'checkbox' ? checked : value
    };

    // If user changed soy or busco, handle non-binary logic more leniently
    if (name === 'soyGender' || name === 'buscoGender') {
      const soy = name === 'soyGender' ? value : newFilters.soyGender;
      const busco = name === 'buscoGender' ? value : newFilters.buscoGender;

      // If the user explicitly selected "No binario", force both to that value
      if (value === 'No binario') {
        newFilters.soyGender = 'No binario';
        newFilters.buscoGender = 'No binario';
        newFilters.sexoBusca = 'No binario';
      } else {
        // If the other field was previously 'No binario', clear it so the user can choose a new value
        const otherWasNonBinary = (name === 'soyGender' ? filters.buscoGender === 'No binario' : filters.soyGender === 'No binario');
        if (otherWasNonBinary) {
          if (name === 'soyGender') newFilters.buscoGender = '';
          else newFilters.soyGender = '';
        }

        // Build combined sexoBusca only when both non-empty and none are 'No binario'
        if (soy && busco && soy !== 'No binario' && busco !== 'No binario') {
          newFilters.sexoBusca = config.buildSexoBusca(soy, busco);
        } else {
          newFilters.sexoBusca = '';
        }
      }
    }

    setFilters(newFilters);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setFiltersApplied(true); // Marcar que los filtros se aplicaron
    filterUsers(1, true); // true = aplicar filtros
  };

  const handleUsernameSearch = async (e) => {
    // Si e es un string, usarlo directamente; si es un evento, prevenir default
    let searchTerm;
    if (typeof e === 'string') {
      searchTerm = e;
    } else {
      e.preventDefault();
      searchTerm = usernameSearch;
    }
    
    if (!searchTerm.trim()) return;
    
    setIsUsernameSearching(true);
    setApiStatus('searching');
    
    try {
      // Usar el nuevo endpoint de búsqueda con parámetro search
      const response = await apiService.searchUsers({ search: searchTerm.trim() }, 1, 12);
      
      // Verificar si hay resultados
      if (!response || !response.users || response.users.length === 0) {
        setSearchResults([]);
        setApiStatus('error');
        setNotification({
          type: 'error',
          message: `No se encontró el usuario "${searchTerm.trim()}" o no está disponible`
        });
        return;
      }
      
      // Transformar resultados al formato del frontend
      const transformedResults = response.users.map(userData => {
        const gender = userData.sex === 'male' ? 'Hombre' : 'Mujer';
        const hasPhoto = !!(userData.profilePicture || userData.profile_picture);
        
        return {
          id: userData.id,
          username: userData.username,
          email: userData.email,
          name: userData.firstName || userData.username || 'Usuario',
          age: userData.age,
          gender: gender,
          state: userData.location || 'Ciudad de México',
          municipio: userData.municipio || '',
          description: userData.bio || '',
          hasPhoto: hasPhoto,
          photoUrl: hasPhoto ? userData.profilePicture : config.getDefaultAvatar(gender),
          isOnline: userData.isOnline === 1 || userData.isOnline === true,
          lastSeen: userData.lastLogin || userData.last_login,
          // Nuevos campos
          estadoCivil: userData.estadoCivil,
          educacion: userData.educacion,
          ocupacion: userData.ocupacion,
          descripcionFisica: userData.descripcionFisica
        };
      });
      
      setSearchResults(transformedResults);
      setApiStatus('success');
      
      // Actualizar paginación si viene en la respuesta
      if (response.pagination) {
        setPagination(response.pagination);
      }
    } catch (error) {
      setSearchResults([]);
      setApiStatus('error');
      
      // Mostrar notificación de error genérico
      setNotification({
        type: 'error',
        message: error.message || 'Error al buscar el usuario. Intenta de nuevo.'
      });
    } finally {
      setIsUsernameSearching(false);
    }
  };

  const loadMoreResults = () => {
    if (currentPage < pagination.pages && !isSearching) {
      filterUsers(currentPage + 1, filtersApplied);
    }
  };

  const clearUsernameSearch = () => {
    setUsernameSearch('');
    setCurrentPage(1);
    filterUsers(1, false); // Sin filtros cuando limpia username
  };

  // Manejar cambios de bloqueo
  const handleBlockChange = (userId, isBlocked) => {
    if (isBlocked) {
      setBlockedUsers(prev => new Set([...prev, userId]));
      // El backend filtra automáticamente, pero actualizamos la UI local
      setSearchResults(prev => prev.filter(user => user.id !== userId));
    } else {
      setBlockedUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
      // Recargar resultados para mostrar el usuario desbloqueado con los mismos filtros aplicados
      filterUsers(1, filtersApplied);
    }
  };

  const resetFilters = () => {
    setFilters({
      minAge: getDefaultMinAge(),
      maxAge: getDefaultMaxAge(),
      state: currentUser?.state || '', // Resetear al estado del usuario
      hasPhoto: false,
      soyGender: currentUser?.sexoBusca ? config.extractSoyFromSexoBusca(currentUser.sexoBusca) : '',
      buscoGender: currentUser?.sexoBusca ? config.extractBuscoFromSexoBusca(currentUser.sexoBusca) : '',
      sexoBusca: currentUser?.sexoBusca || ''
    });
    setSearchResults([]);
    setCurrentPage(1);
    setPagination({
      page: 1,
      limit: 12, // Cambiado a 12 por página
      total: 0,
      pages: 0
    });
    setApiStatus('idle');
    setFiltersApplied(false); // Resetear flag de filtros aplicados
  };

  return (
    <div className="min-h-screen bg-background py-8">
      {/* Notificación temporal */}
      {notification && (
        <div className="fixed top-4 right-4 z-50 max-w-md">
          <div className={`p-4 rounded-lg shadow-lg border-l-4 ${
            notification.type === 'error' 
              ? 'bg-red-50 border-red-400 text-red-800' 
              : 'bg-green-50 border-green-400 text-green-800'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-lg">
                  {notification.type === 'error' ? '⚠️' : '✅'}
                </span>
                <p className="font-medium">{notification.message}</p>
              </div>
              <button
                onClick={() => setNotification(null)}
                className="text-gray-400 hover:text-gray-600 ml-4"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <Search className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold text-text">Buscar Pareja</h1>
          </div>
          <p className="text-gray-600">
            Encuentra a tu pareja ideal usando nuestros filtros avanzados.
          </p>
          
          
          {/* Estado de conexión */}
          <div className="mt-2 flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${
              apiStatus === 'success' ? 'bg-green-500' : 
              apiStatus === 'error' ? 'bg-red-500' : 
              apiStatus === 'searching' ? 'bg-yellow-500' : 'bg-gray-400'
            }`}></div>
            <span className="text-sm text-gray-600">
              {apiStatus === 'success' ? 'Búsqueda completada' :
               apiStatus === 'error' ? 'Error en la búsqueda' :
               apiStatus === 'searching' ? 'Buscando...' : 'Listo para buscar'}
            </span>
          </div>
        </div>

        {/* Filtros */}
        <div className="card mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-text flex items-center space-x-2">
              <Filter className="h-5 w-5" />
              <span>Filtros de búsqueda</span>
            </h2>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="btn-outline hover:scale-105 active:scale-95 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              {showFilters ? 'Ocultar' : 'Mostrar'} Filtros
            </button>
          </div>

          {showFilters && (
            <form onSubmit={handleSearch} className="space-y-6">
              {/* Filtro "Muéstrame" - Justo debajo del título */}
              <div className="mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Yo soy</label>
                    <select
                      name="soyGender"
                      value={filters.soyGender}
                      onChange={handleFilterChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="">Seleccionar (Yo soy)</option>
                      {config.GENDER_OPTIONS.map((option, index) => (
                        <option key={`soy-${index}`} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Buscando</label>
                    <select
                      name="buscoGender"
                      value={filters.buscoGender}
                      onChange={handleFilterChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="">Seleccionar (Busco)</option>
                      {config.GENDER_OPTIONS.map((option, index) => (
                        <option key={`busco-${index}`} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Filtra usuarios compatibles según tu preferencia
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

                {/* Estado */}
                <div>
                  <label className="form-label">Estado</label>
                  <select
                    name="state"
                    value={filters.state}
                    onChange={handleFilterChange}
                    className="input-field"
                  >
                    <option value="">Todos los estados</option>
                    <option value="Extranjero">Extranjero</option>
                    {ubicacionesMexico.map((ubicacion, index) => (
                      <option key={`${ubicacion}-${index}`} value={ubicacion}>{ubicacion}</option>
                    ))}
                  </select>
                </div>

                {/* Rango de edad */}
                <div>
                  <label className="form-label">Edad mínima</label>
                  <input
                    type="number"
                    name="minAge"
                    value={filters.minAge}
                    onChange={handleFilterChange}
                    className="input-field"
                    min="18"
                    max="100"
                  />
                </div>

                <div>
                  <label className="form-label">Edad máxima</label>
                  <input
                    type="number"
                    name="maxAge"
                    value={filters.maxAge}
                    onChange={handleFilterChange}
                    className="input-field"
                    min={filters.minAge}
                    max="100"
                  />
                </div>
                
                {/* Has Photo Toggle */}
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="hasPhoto"
                    name="hasPhoto"
                    checked={filters.hasPhoto}
                    onChange={handleFilterChange}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <label htmlFor="hasPhoto" className="text-sm text-gray-700">
                    Solo usuarios con foto
                  </label>
                </div>
              </div>

                             <div className="flex space-x-4">
                 <button
                   type="submit"
                   disabled={isSearching}
                   className="btn-primary flex items-center space-x-2 hover:scale-105 active:scale-95 transition-all duration-200 shadow-md hover:shadow-lg"
                 >
                   {isSearching ? (
                     <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                   ) : (
                     <Search className="h-4 w-4" />
                   )}
                   <span>{isSearching ? 'Buscando...' : 'Buscar'}</span>
                 </button>
                 <button
                   type="button"
                   onClick={resetFilters}
                   className="btn-outline hover:scale-105 active:scale-95 transition-all duration-200 shadow-md hover:shadow-lg"
                 >
                   Limpiar Filtros
                 </button>
               </div>
            </form>
          )}
        </div>

        {/* Búsqueda por Username */}
        <div className="card mb-8">
          <h2 className="text-xl font-semibold text-text mb-4 flex items-center space-x-2">
            <Search className="h-5 w-5" />
            <span>Búsqueda por Username</span>
          </h2>
          <form onSubmit={handleUsernameSearch} className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <input
                type="text"
                value={usernameSearch}
                onChange={(e) => setUsernameSearch(e.target.value)}
                placeholder="Ingresa el username exacto (ej: badboy_29)"
                className="input-field w-full"
                disabled={isUsernameSearching}
              />
            </div>
            <div className="flex gap-4">
              <button
                type="submit"
                disabled={isUsernameSearching || !usernameSearch.trim()}
                className="btn-primary flex items-center space-x-2 hover:scale-105 active:scale-95 transition-all duration-200 shadow-md hover:shadow-lg whitespace-nowrap"
              >
                {isUsernameSearching ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <Search className="h-4 w-4" />
                )}
                <span>{isUsernameSearching ? 'Buscando...' : 'Buscar'}</span>
              </button>
              {usernameSearch && (
                <button
                  type="button"
                  onClick={clearUsernameSearch}
                  className="btn-outline hover:scale-105 active:scale-95 transition-all duration-200 shadow-md hover:shadow-lg whitespace-nowrap"
                >
                  Limpiar
                </button>
              )}
            </div>
          </form>
          <p className="text-sm text-gray-500 mt-2">
            Busca un usuario específico por su username exacto
          </p>
        </div>

        {/* Resultados */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-text">
              Resultados ({searchResults.length}{pagination.total > searchResults.length ? ` de ${pagination.total}` : ''})
            </h2>
            
            {searchResults.length > 0 && (
              <p className="text-gray-600">
                Página {pagination.page} de {pagination.pages}
              </p>
            )}
          </div>

          {searchResults.length === 0 && !isSearching ? (
            <div className="card text-center py-12">
              <Search className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-text mb-2">
                No se encontraron resultados
              </h3>
              <p className="text-gray-600 mb-4">
                Intenta ajustar tus filtros de búsqueda o verifica tu conexión
              </p>
                             <button 
                 onClick={resetFilters}
                 className="btn-primary hover:scale-105 active:scale-95 transition-all duration-200 shadow-md hover:shadow-lg"
               >
                 Limpiar Filtros
               </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {searchResults.map((user) => (
                <Link
                  key={user.id}
                  to={`/perfil/${user.id}`}
                  className="card hover:shadow-lg hover:scale-105 transition-all duration-300 transform cursor-pointer group block"
                >
                  <div className="relative mb-4">
                    <img
                      src={user.photoUrl || 'https://via.placeholder.com/300x300?text=?'}
                      alt={user.name}
                      className="w-full h-48 object-cover rounded-lg group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute top-2 right-2">
                      <div className={`w-3 h-3 rounded-full border-2 border-white ${
                        user.isOnline ? 'bg-green-500' : 'bg-gray-400'
                      }`}></div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <h3 className="font-semibold text-lg text-text group-hover:text-primary transition-colors duration-200">{user.name}</h3>
                    <p className="text-sm text-gray-500">@{user.username}</p>
                    <div className="flex items-center space-x-2 text-gray-600">
                      <Calendar className="h-4 w-4" />
                      <span>{user.age} años</span>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-600">
                      <MapPin className="h-4 w-4" />
                      <span>{user.state}{user.municipio && `, ${user.municipio}`}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-600">
                      <Heart className="h-4 w-4" />
                      <span>{user.gender}</span>
                    </div>
                    
                    {isAdmin && user.email && (
                      <div className="flex items-start space-x-2 text-gray-600 text-sm">
                        <svg className="h-4 w-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <span className="break-all">{user.email}</span>
                      </div>
                    )}
                    
                    {user.educacion && (
                      <div className="flex items-center space-x-2 text-gray-600">
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                          {user.educacion}
                        </span>
                      </div>
                    )}
                    
                    {user.ocupacion && (
                      <div className="flex items-center space-x-2 text-gray-600">
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                          {user.ocupacion}
                        </span>
                      </div>
                    )}
                    
                    {user.description && (
                      <p className="text-gray-600 text-sm line-clamp-2">
                        {user.description}
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}

                     {/* Botón Cargar Más */}
           {searchResults.length > 0 && currentPage < pagination.pages && (
             <div className="text-center mt-8">
               <button
                 onClick={loadMoreResults}
                 disabled={isSearching}
                 className="btn-primary hover:scale-105 active:scale-95 transition-all duration-200 shadow-md hover:shadow-lg"
               >
                 {isSearching ? (
                   <div className="flex items-center space-x-2">
                     <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                     <span>Cargando...</span>
                   </div>
                 ) : (
                   `Cargar más (${pagination.total - searchResults.length} restantes)`
                 )}
               </button>
             </div>
           )}
        </div>
      </div>
    </div>
  );
};

export default SearchPartner;
